import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import { ReactSVG } from "react-svg";
import Swal from 'sweetalert2/src/sweetalert2.js';
import { info, warning } from "@alert";
import CheckoutCollapseChevron from "@components/Checkout/CheckoutCollapseChevron";
import {
  resolveShiftSchedulePermissionSubKey,
  resolveOptionsMenuPermission,
} from "@helpers/checkPermission";
import ModuleDefaultHomeIndicator from "@components/ModuleDefaultHome/ModuleDefaultHomeIndicator";
import { menuEngMatchesDefaultHomePath } from "@helpers/moduleDefaultHomeMenu";
import {
  getGroupDefaultHomeConfig,
  getSavedGroupDefaultPath,
  hasUserConfiguredGroupDefault,
  resolveGroupDefaultPathForNavigation,
} from "@helpers/groupDefaultPath";
import { normalizeModulePath } from "@helpers/moduleDefaultPath";
import { scrollMenuBarAccordionFromEvent } from "@utils/scrollMenuBarAccordionIntoView";

/** 左欄圖示載入中占位（尺寸與正式圖示區一致，避免未載入時跑版） */
function DropDownOptionIconLoading() {
  return <span className="drop-down-option__icon-skeleton" aria-hidden />;
}

/**
 * 測試骨架／loading 用：將下方改為 true 可固定顯示圖示 loading（勿提交 true）。
 * 或於 .env 設 REACT_APP_DEBUG_FORCE_SIDEBAR_ICON_LOADING=true 後重啟 dev server。
 */
const DEBUG_LOCAL_FORCE_MENU_ICON_LOADING = false;
const DEBUG_FORCE_SIDEBAR_ICON_LOADING =
  DEBUG_LOCAL_FORCE_MENU_ICON_LOADING ||
  process.env.REACT_APP_DEBUG_FORCE_SIDEBAR_ICON_LOADING === "true";

function renderSettingsMenuIcon(item) {
  const slotClass = "drop-down-option__icon-slot";
  const src =
    process.env.PUBLIC_URL +
    "/assets/img/icon/settings-menu/" +
    item.icon +
    ".svg";

  if (DEBUG_FORCE_SIDEBAR_ICON_LOADING) {
    return (
      <div className={slotClass}>
        <div className="drop-down-option__icon-svg">
          <DropDownOptionIconLoading />
        </div>
      </div>
    );
  }

  return (
    <div className={slotClass}>
      <ReactSVG
        src={src}
        className="drop-down-option__icon-svg"
        loading={DropDownOptionIconLoading}
      />
    </div>
  );
}

function DropDownOption({
  list,
  handleMenuClick,
  toggle,
  setToggle,
  pagination,
  routerName,
  defaultHomePath,
  showDefaultHomeIndicator,
}) {
  const getDisplayTitle = (menuItem) => menuItem.displayTitle || menuItem.title;
  const permissions = useSelector(state => state.user.user.permissions)

  const labelMapping = {
    shiftschedule: "班表",
    options: "設定",
    /** 與 checkPermission CATEGORY_MAP.tickets →「課程」、API permissions 鍵名一致（勿用「票券」） */
    tickets: "課程",
    sales: "銷售",
    reservation: "預約",
    customer: "會員",
    products: "商品",
    statistics: "數據",
    info: "設定",
    account: "設定",
    memberInteraction: "設定",
    discount: "設定",
  };

  // 暫時跳過權限檢查的側欄標題（後端尚未建立權限時使用）
  const skipPermissionCheck = [
    "修改密碼",
  ];

  /** 平板側欄較窄時字級減 2px（與父層 1em 對齊） */
  const submenuTitlesTabletMinus2px = new Set([
    "對應服務分類",
    "服務同意書關聯設定",
  ]);

  // 永遠允許的頁面（不顯示灰色樣式，所有人都可造訪）
  const alwaysAllowedItems = [
    "公司資訊",
    "喜好設定",
    "修改密碼",
  ];

  // 側邊欄標題到權限系統標題的映射（當側邊欄標題與權限系統不一致時使用）
  const titleToPermissionMapping = {
    "核銷明細報表": "訂單業績明細",  // 核銷明細報表無獨立權限，沿用訂單業績明細
    "回饋金/點數管理報表": "訂單業績明細",
    "虛擬業績報表": "業績報表", // 虛擬業績報表無獨立權限，沿用業績報表
    /** 側欄若顯示「分店資訊」，權限物件仍為後端鍵名「店家資訊」 */
    "分店資訊": "店家資訊",
    /**
     * 側欄「訊息設定」僅為 UI 分組（內含自動化訊息設定路由），不對應 Redux「設定」下名為「訊息設定」的第二層子鍵（該鍵不存在）。
     * 可否展開／點入與「自動化訊息設定」第二層區塊一致（查看等子項由 checkSecondLevelPermission 合併檢查）。
     * 巢狀於「自動化訊息設定」底下的第三層「訊息管理」為帳號權限／API 所列之 function，並非此側欄按鈕的獨立開關。
     */
    "訊息設定": "自動化訊息設定",
  };

  function getMenuPermissionSubKey(menuItem, parentTitle) {
    if (parentTitle === "班表" && menuItem?.eng) {
      return resolveShiftSchedulePermissionSubKey(menuItem.eng, menuItem.title);
    }
    if (parentTitle === "設定") {
      const optionsPermission = resolveOptionsMenuPermission(menuItem.title);
      if (optionsPermission) return optionsPermission.subKey;
    }
    return titleToPermissionMapping[menuItem.title] || menuItem.title;
  }

  /** 第三層非「查看」的權限（如 病歷管理／罐頭訊息）需指定方法鍵名 */
  function getMenuPermissionMethodKey(menuItem, parentTitle) {
    if (parentTitle !== "設定") return null;
    return resolveOptionsMenuPermission(menuItem.title)?.methodKey || null;
  }

  function checkMenuItemPermission(parentTitle, menuItem, permissions) {
    if (skipPermissionCheck.includes(menuItem.title)) {
      return true;
    }
    return checkSecondLevelPermission(
      parentTitle,
      getMenuPermissionSubKey(menuItem, parentTitle),
      permissions,
      getMenuPermissionMethodKey(menuItem, parentTitle)
    );
  }

  function checkSecondLevelPermission(parentTitle, childTitle, permissions, methodKey) {
    // 如果在跳過列表中，直接返回 true
    if (skipPermissionCheck.includes(childTitle)) {
      return true;
    }

    // 如果有映射，使用映射後的標題檢查權限
    const mappedChildTitle = titleToPermissionMapping[childTitle] || childTitle;
    const parentPermissions = permissions?.[parentTitle];

    if (!parentPermissions) {
      return false;
    }

    const childPermissions = parentPermissions?.[mappedChildTitle];

    // 如果子層不存在，返回 false
    if (!childPermissions) {
      return false;
    }

    // 指定方法鍵名時只認該方法（如 病歷管理 底下各頁以自己的方法控管）
    if (methodKey) {
      return childPermissions[methodKey]?.enable === true;
    }

    // 檢查是否有更深層的權限（如「團隊成員」有「查看」、「新增」等子權限）
    // 方法：檢查是否有任何屬性的值是物件且包含 enable 屬性
    const hasSubPermissions = Object.keys(childPermissions).some(
      key => key !== 'enable' && typeof childPermissions[key] === 'object' && childPermissions[key]?.hasOwnProperty('enable')
    );
    
    if (hasSubPermissions) {
      // 如果有子權限，檢查是否至少有一個子權限是啟用的
      // 優先檢查「查看」權限
      if (childPermissions["查看"]?.enable) {
        return true;
      }
      
      // 如果沒有「查看」權限，檢查是否有任何一個子權限是啟用的
      const hasAnyEnabledPermission = Object.keys(childPermissions).some(
        key => key !== 'enable' && childPermissions[key]?.enable === true
      );
      
      return hasAnyEnabledPermission;
    }
    
    // 如果沒有子權限，直接檢查 enable 屬性（適用於單層權限，如「業績報表」）
    return childPermissions.enable ?? false;
  }

  function isDescendantActive(menuItem, currentPagination) {
    if (currentPagination === menuItem.eng) {
      return true;
    }
    return menuItem.items?.some((child) => isDescendantActive(child, currentPagination)) ?? false;
  }

  function hasAnyDescendantPermission(menuItem, parentTitle, userPermissions) {
    if (skipPermissionCheck.includes(menuItem.title)) {
      return true;
    }
    if (!menuItem.items?.length) {
      return checkMenuItemPermission(parentTitle, menuItem, userPermissions);
    }
    return menuItem.items.some((child) =>
      hasAnyDescendantPermission(child, parentTitle, userPermissions)
    );
  }

  function findFirstNavigableEng(menuItem, parentTitle, userPermissions) {
    if (!menuItem.items?.length) {
      return null;
    }
    for (const child of menuItem.items) {
      if (child.items?.length) {
        if (hasAnyDescendantPermission(child, parentTitle, userPermissions)) {
          return child.eng;
        }
      } else if (checkMenuItemPermission(parentTitle, child, userPermissions)) {
        return child.eng;
      }
    }
    return null;
  }

  const mappedParentTitle = labelMapping[list.label] || list.label;

  const isDefaultHomeEng = (eng) =>
    Boolean(
      showDefaultHomeIndicator &&
        routerName &&
        menuEngMatchesDefaultHomePath(defaultHomePath, routerName, eng)
    );

  const isGroupDefaultEng = (groupEng, leafEng) => {
    if (!routerName || !groupEng || !leafEng) return false;
    if (!hasUserConfiguredGroupDefault(routerName, groupEng)) return false;
    const saved = getSavedGroupDefaultPath(routerName, groupEng);
    return (
      normalizeModulePath(saved) ===
      normalizeModulePath(`/${routerName}/${leafEng}`)
    );
  };

  const resolveClickEngForGroup = (item) => {
    const groupConfig = getGroupDefaultHomeConfig(routerName, item.eng);
    if (groupConfig) {
      const { eng } = resolveGroupDefaultPathForNavigation(
        routerName,
        item.eng,
        permissions
      );
      if (eng) return eng;
    }
    if (item.groupOnly || item.eng === "message") {
      if (item.eng === "message") return item.items?.[0]?.eng || null;
      return findFirstNavigableEng(item, mappedParentTitle, permissions);
    }
    return item.eng;
  };

  function renderSubmenuLeaf(subItem, parentEng) {
    const subHasPermission = checkMenuItemPermission(
      mappedParentTitle,
      subItem,
      permissions
    );
    const showGroupDefaultStar = isGroupDefaultEng(parentEng, subItem.eng);

    return (
      <Fragment key={subItem.eng}>
        <li
          onClick={() => {
            if (!checkMenuItemPermission(mappedParentTitle, subItem, permissions)) {
              warning(1,
                 "權限不足 !",
                 "請使用有更高權限的帳號進行此操作",
                 "我了解了",
               );
              return;
            }

            setToggle((prev) => ({
              ...prev,
              [parentEng]: true,
            }));
            handleMenuClick(subItem.eng);
          }}
          className={`${pagination === subItem.eng ? "submenu-item-active" : ""}${
            !subHasPermission
              ? " drop-down-option__submenu-item--no-permission"
              : ""
          }${
            submenuTitlesTabletMinus2px.has(getDisplayTitle(subItem))
              ? " drop-down-option__submenu-title--tablet-2"
              : ""
          }`.trim()}
        >
          {getDisplayTitle(subItem)}
          {/* 同色實心星只保留區塊首頁；分組預設改空心，且與區塊首頁重疊時不重複顯示 */}
          <ModuleDefaultHomeIndicator
            visible={isDefaultHomeEng(subItem.eng)}
            variant="filled"
          />
          <ModuleDefaultHomeIndicator
            visible={showGroupDefaultStar && !isDefaultHomeEng(subItem.eng)}
            variant="outline"
          />
        </li>
        <hr className="m-0" />
      </Fragment>
    );
  }

  function renderSubmenuEntry(subItem, parentEng) {
    if (subItem.items?.length) {
      const nestedHasPermission = hasAnyDescendantPermission(
        subItem,
        mappedParentTitle,
        permissions
      );
      const nestedActive = isDescendantActive(subItem, pagination);

      return (
        <Fragment key={subItem.eng}>
          <li
            className={`submenu-group${
              nestedActive ? " submenu-group--active" : ""
            }${
              !nestedHasPermission
                ? " drop-down-option__submenu-item--no-permission"
                : ""
            }`}
          >
            <div className="submenu-group__row">
              <span
                className="submenu-group__label"
                onClick={(e) => {
                  if (!nestedHasPermission) {
                    warning(1, 
                      "權限不足", 
                      "請使用有更高權限的帳號進行此操作", 
                      "我了解了"
                    );
                    return;
                  }

                  const willOpen = !toggle[subItem.eng];
                  setToggle((prev) => ({
                    ...prev,
                    [parentEng]: true,
                    [subItem.eng]: true,
                  }));
                  const firstEng = findFirstNavigableEng(
                    subItem,
                    mappedParentTitle,
                    permissions
                  );
                  if (firstEng) handleMenuClick(firstEng);
                  if (willOpen) {
                    scrollMenuBarAccordionFromEvent(e.currentTarget);
                  }
                }}
              >
                {getDisplayTitle(subItem)}
              </span>
              <button
                type="button"
                className="drop-down-option__accordion-toggle submenu-group__toggle"
                aria-expanded={Boolean(toggle[subItem.eng])}
                aria-label={
                  toggle[subItem.eng] ? "收合子選單" : "展開子選單"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = !toggle[subItem.eng];
                  setToggle((prev) => ({
                    ...prev,
                    [parentEng]: true,
                    [subItem.eng]: !prev[subItem.eng],
                  }));
                  if (willOpen) {
                    scrollMenuBarAccordionFromEvent(e.currentTarget);
                  }
                }}
              >
                <CheckoutCollapseChevron
                  expanded={Boolean(toggle[subItem.eng])}
                  size={20}
                  color={nestedHasPermission ? "#6b7280" : "#b8bec8"}
                />
              </button>
            </div>
            <div
              className={`submenu-wrapper submenu-wrapper--nested ${
                toggle[subItem.eng] ? "drop" : ""
              }`}
            >
              <div className="submenu-wrapper__inner">
                <ul className="submenu submenu--nested">
                  {subItem.items.map((leaf) => renderSubmenuLeaf(leaf, parentEng))}
                </ul>
              </div>
            </div>
          </li>
          <hr className="m-0" />
        </Fragment>
      );
    }

    return renderSubmenuLeaf(subItem, parentEng);
  }

  const itemShowsDefaultStar = (item) => {
    if (!isDefaultHomeEng(item.eng)) return false;
    if (!item.items?.length) return true;

    const descendantMatches = item.items.some((sub) => {
      if (sub.items?.length) {
        return sub.items.some((leaf) => isDefaultHomeEng(leaf.eng));
      }
      return isDefaultHomeEng(sub.eng);
    });

    return !descendantMatches;
  };

  return (
    <div className="drop-down-option mb-2">
      <ul className="drop-down-option__buttons">
        {list.items.map((item, index) => {
          // 檢查是否為永遠允許的項目（如公司資訊）
          const isAlwaysAllowed = alwaysAllowedItems.includes(item.title);
          const itemHasPermission =
            item.eng === "manual" || isAlwaysAllowed
              ? true
              : item.groupOnly
                ? hasAnyDescendantPermission(item, mappedParentTitle, permissions)
                : checkMenuItemPermission(mappedParentTitle, item, permissions);
          const itemIsActive = item.items?.length
            ? isDescendantActive(item, pagination)
            : pagination === item.eng;

          return (
            // 以下加入 index 來暫時確保內有重富的 key 值
            <Fragment key={`${item.icon}-${index}`}>
              <li
                className={`d-flex flex-column drop-down-item${
                  item.items && item.items.length > 0
                    ? " drop-down-item--has-submenu"
                    : ""
                }${itemIsActive ? " drop-down-item--active" : ""
                }${!itemHasPermission ? " drop-down-option__item--no-permission" : ""}`}
                style={
                  (() => {
                    if (!itemHasPermission) {
                      return {
                        backgroundColor: "#eceff1",
                        transition: "background-color 0.2s ease",
                      };
                    }
                    // 如果 item 有子項目，檢查 pagination 是否落在子樹內
                    if (item.items && item.items.length > 0) {
                      const isChildActive = isDescendantActive(item, pagination);
                      if (isChildActive) {
                        return { backgroundColor: "#F0F5FA", transition: "background-color 0.2s ease" };
                      }
                    }

                    // 只有當 pagination === item.eng 時，才顯示深藍色背景
                    // 注意：這與手風琴展開狀態無關，只與當前路由有關
                    if (pagination === item.eng) {
                      return { backgroundColor: "#E6EEF6", transition: "background-color 0.2s ease" };
                    }

                    // 預設返回 null，不顯示任何背景色
                    return null;
                  })()
                }
              >
                {/* 以下 manual 為設定 - OBS 操作手冊（外開 GitBook） */}
                {item.eng === "manual" ? (
                  <a
                    href="https://obsicloud-apamverse.gitbook.io/manual"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-fix"
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#88add4';
                      e.currentTarget.style.color = '#ffffff';
                      // 修改內部 SVG 顏色
                      const svgs = e.currentTarget.querySelectorAll('svg, svg path');
                      svgs.forEach(svg => {
                        svg.style.fill = '#ffffff';
                      });
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '';
                      e.currentTarget.style.color = '';
                      // 還原內部 SVG 顏色
                      const svgs = e.currentTarget.querySelectorAll('svg, svg path');
                      svgs.forEach(svg => {
                        svg.style.fill = '';
                      });
                    }}
                  >
                    <div
                      className="d-flex justify-content-between align-items-center p-1"
                      onClick={() => {
                        setToggle((prev) => ({
                          ...prev,
                          [item.eng]: !prev[item.eng],
                        }));
                        handleMenuClick(item.eng);
                      }}
                    >
                      <div className="d-flex align-items-center">
                        {renderSettingsMenuIcon(item)}
                        {getDisplayTitle(item)}
                      </div>
                      {/* 外部連結圖示 */}
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 16 16"
                        fill="gray"
                        style={{ marginRight: '8px' }}
                      >
                        <path d="M13.5 1h-3a.5.5 0 0 0 0 1h1.793l-5.147 5.146a.5.5 0 0 0 .708.708L13 2.707V4.5a.5.5 0 0 0 1 0v-3a.5.5 0 0 0-.5-.5z" />
                        <path d="M12 8.5v4a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h4a.5.5 0 0 0 0-1h-4A1.5 1.5 0 0 0 1 3.5v9A1.5 1.5 0 0 0 2.5 14h9a1.5 1.5 0 0 0 1.5-1.5v-4a.5.5 0 0 0-1 0z" />
                      </svg>
                    </div>
                  </a>
                ) : (
                  <div className="drop-down-option__row p-1">
                    <div
                      className="d-flex align-items-center drop-down-option__row-label"
                      onClick={(e) => {
                        const mappedChildTitle = item.title;

                        // 檢查是否為永遠允許的項目
                        const isAlwaysAllowed = alwaysAllowedItems.includes(mappedChildTitle);
                        
                        // 檢查權限（永遠允許的項目跳過權限檢查）
                        const hasPermission = isAlwaysAllowed || itemHasPermission;

                        if (!hasPermission) {
                          const isShiftSchedulePassword = list.label === "shiftschedule" && item.title === "修改密碼";
                          if (isShiftSchedulePassword) {
                            info(1, 
                              "若需要本功能，請聯絡我們", 
                              "", 
                              "我了解了"
                            );
                          } else {
                            warning(1,
                              "權限不足",
                              "請使用有更高權限的帳號進行此操作",
                              "我了解了"
                            );
                          }
                          return;
                        }

                        // 如果有子選單，檢查是否需要自動跳轉
                        if (item.items && item.items.length > 0) {
                          if (
                            item.groupOnly ||
                            item.eng === "message" ||
                            getGroupDefaultHomeConfig(routerName, item.eng)
                          ) {
                            // 訊息設定：展開方便看到子項；病歷／同意書／問卷：只跳轉，不強制展開
                            if (item.eng === "message") {
                              const willOpen = !toggle[item.eng];
                              setToggle((prev) => ({
                                ...prev,
                                [item.eng]: true,
                              }));
                              if (willOpen) {
                                scrollMenuBarAccordionFromEvent(e.currentTarget);
                              }
                            }
                            const targetEng = resolveClickEngForGroup(item);
                            if (targetEng) {
                              handleMenuClick(targetEng);
                            }
                          } else {
                            // 其他有子選單的項目（如服務上架）：跳轉到父項目本身
                            handleMenuClick(item.eng);
                          }
                        } else {
                          // 沒有子選單則直接跳轉
                          handleMenuClick(item.eng);
                        }
                      }}
                    >
                      {renderSettingsMenuIcon(item)}
                      {getDisplayTitle(item)}
                    </div>
                    <ModuleDefaultHomeIndicator
                      visible={itemShowsDefaultStar(item)}
                    />
                    {item.items && item.items.length > 0 && (
                      <button
                        type="button"
                        className="drop-down-option__accordion-toggle"
                        aria-expanded={Boolean(toggle[item.eng])}
                        aria-label={
                          toggle[item.eng] ? "收合子選單" : "展開子選單"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          const willOpen = !toggle[item.eng];
                          setToggle((prev) => ({
                            ...prev,
                            [item.eng]: !prev[item.eng],
                          }));
                          if (willOpen) {
                            scrollMenuBarAccordionFromEvent(e.currentTarget);
                          }
                        }}
                      >
                        <CheckoutCollapseChevron
                          expanded={Boolean(toggle[item.eng])}
                          size={20}
                          color={itemHasPermission ? "#6b7280" : "#b8bec8"}
                        />
                      </button>
                    )}
                  </div>
                )}

                {/* 以下為設定 - 訊息 - 自動化訊息設定 */}
                <div
                  className={`submenu-wrapper ${toggle[item.eng] ? "drop" : ""
                    }`}
                >
                  {item.items ? (
                    <div className="submenu-wrapper__inner">
                    <ul className="submenu">
                      {item.items.map((subItem) => renderSubmenuEntry(subItem, item.eng))}
                    </ul>
                    </div>
                  ) : null}
                </div>
              </li>
              <hr className="m-0" />
            </Fragment>
          );
        })}
      </ul>
    </div>
  );
}

export default DropDownOption;
